import { z } from 'zod';
import { getAvatarImageURL } from 'lib/media/imageHelper';
import type { CustomFieldType, Event, Pin, Resource, Status } from '__types__';
import type { ResourceSearchQuery } from 'v5/api/resource/resource.api';
import { shortlistActionSchema } from 'v4/entities/shortlist/shortlist.types';
import {
  shortlist$,
  shortlistFromHttp
} from '../shortlist/shortlist.http.schema';
import type {
  BoardShortlist,
  ClientShortlist,
  ClientSummary,
  ShortlistBlockCard,
  ShortlistClientView,
  ShortlistSummary
} from './shortlistBoard.api';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/** Nested legacy models travel as they are; only their id is checked. */
const withId = <T>() =>
  z.custom<T>(value => isRecord(value) && typeof value.id === 'number');

/**
 * The resource search a v4 star saved, kept whole (v4 still reads all of it); only its shape is
 * checked. A value of another shape reads as no saved search, not as a broken shortlist.
 */
const savedResourceSearch$ = z
  .custom<ResourceSearchQuery>(
    value =>
      isRecord(value) &&
      (value.filters === undefined || isRecord(value.filters))
  )
  .nullable()
  .catch(null);

const layout$ = z.enum(['PAGE', 'GRID', 'COMPACT']).nullable();

/** The shortlist in the client view: no internal ids, password, owner or notes. */
export const clientShortlist$ = z.object({
  public_id: z.string(),
  public_title: z.string().nullable(),
  public_description: z.string().nullable(),
  default_fields: z.array(z.string()).nullable(),
  card_field_count: z.number(),
  enable_voting: z.boolean().nullable(),
  enable_commenting: z.boolean().nullable(),
  public_feedback: z.boolean().nullable(),
  layout: layout$,
  show_availability: z.unknown().nullable().optional(),
  // only its filters on the card fields
  default_resources_query: savedResourceSearch$,
  block_count: z.number(),
  tags: z.array(z.object({ id: z.number(), tag: z.string() })).nullish(),
  created_at: z.string().nullish(),
  updated_at: z.string().nullish()
});

export const shortlistSummary$ = shortlist$.extend({
  public_id: z.string(),
  // the summary is the shortlist without its blocks
  shortlist_blocks: z.array(z.unknown()).nullable().default(null),
  block_count: z.number(),
  feedback_count: z.number().default(0),
  card_field_count: z.number(),
  default_resources_query: savedResourceSearch$
});

const qr$ = z
  .object({
    mapping: z.object({
      gender: z.number().nullable(),
      bust: z.number().nullable(),
      waist: z.number().nullable(),
      height: z.number().nullable(),
      shoe: z.number().nullable(),
      bra_size: z.number().nullable()
    }),
    unit: z.enum(['cm', 'in'])
  })
  .nullish()
  .transform(qr => qr ?? null);

const version$ = z.enum(['v5', 'legacy']);

/** Edit's summary: the shortlist as the model formats it, without its blocks. */
export const boardSummary$ = z.object({
  restricted: z.literal(false),
  version: version$,
  shortlist: shortlistSummary$,
  qr: qr$
});

/** v1's `layout` as the client's card size; v4 shows a shortlist without one as its "Grid". */
export const clientViewFromHttp = (
  layout: z.infer<typeof layout$>
): ShortlistClientView => {
  switch (layout) {
    case 'PAGE':
      return { size: 'LARGE' };
    case 'COMPACT':
      return { size: 'SMALL' };
    case 'GRID':
    case null:
      return { size: 'MEDIUM' };
  }
};

const layoutOfSize: Record<
  ShortlistClientView['size'],
  NonNullable<z.infer<typeof layout$>>
> = { LARGE: 'PAGE', MEDIUM: 'GRID', SMALL: 'COMPACT' };

/** The client's card size as v1's `layout`: Large was "Page", Medium "Grid", Small "Compact". */
export const clientViewToHttp = ({ size }: ShortlistClientView) => ({
  layout: layoutOfSize[size]
});

const availabilityOf = (value: unknown): ClientShortlist['showAvailability'] =>
  isRecord(value) &&
  typeof value.start_date === 'string' &&
  typeof value.end_date === 'string'
    ? { startDate: value.start_date, endDate: value.end_date }
    : null;

export const clientShortlistFromHttp = (
  data: z.infer<typeof clientShortlist$>
): ClientShortlist => ({
  publicId: data.public_id,
  title: data.public_title ?? null,
  description: data.public_description ?? null,
  defaultFields: data.default_fields ?? [],
  cardFieldCount: data.card_field_count,
  clientView: clientViewFromHttp(data.layout),
  showAvailability: availabilityOf(data.show_availability),
  enableVoting: Boolean(data.enable_voting),
  enableCommenting: Boolean(data.enable_commenting),
  publicFeedback: Boolean(data.public_feedback),
  clientDefaultQuery: data.default_resources_query ?? null,
  blockCount: data.block_count,
  tags: (data.tags ?? []).map(tag => tag.tag),
  updatedAt: data.updated_at ?? data.created_at ?? null
});

export const shortlistSummaryFromHttp = (
  data: z.infer<typeof shortlistSummary$>,
  qr: ShortlistSummary['qr']
): ShortlistSummary => {
  const { block_count, feedback_count, card_field_count, ...shortlist } = data;
  const board: BoardShortlist = {
    ...clientShortlistFromHttp({
      ...data,
      default_fields: data.default_fields ?? null,
      layout: data.layout ?? null,
      enable_voting: data.enable_voting ?? null,
      enable_commenting: data.enable_commenting ?? null,
      public_feedback: data.public_feedback ?? null,
      public_title: data.public_title ?? null,
      public_description: data.public_description ?? null,
      show_availability: data.show_availability ?? null
    }),
    id: data.id,
    workspaceId: data.enterprise_workspace_id ?? null,
    teamId: data.enterprise_team_id ?? null
  };
  return {
    ...shortlistFromHttp(shortlist),
    board,
    blockCount: block_count,
    feedbackCount: feedback_count,
    cardFieldCount: card_field_count,
    clientView: board.clientView,
    qr
  };
};

export const blockCard$ = z.object({
  id: z.number(),
  contact_id: z.number(),
  order: z.number().nullable(),
  data: z.object({ paragraph: z.string().nullish() }).passthrough().nullish(),
  contact: withId<Resource & { list_counts?: Record<string, number> }>()
    .nullable()
    .default(null),
  pins: z.array(withId<Pin>()),
  pin_count: z.number(),
  upvotes: z.array(shortlistActionSchema),
  downvotes: z.array(shortlistActionSchema),
  comments: z.array(
    shortlistActionSchema.extend({
      // a member's name and picture; a guest's comment carries their own name
      author: z
        .object({ name: z.string(), picture_url: z.string().nullable() })
        .nullish()
    })
  ),
  upvote_count: z.number(),
  downvote_count: z.number(),
  comment_count: z.number(),
  booking_events: z.array(withId<Event>()).optional()
});

/** How many pictures a card brings along when its page is asked for them: the strips draw that many. */
export const PREVIEW_PINS_LIMIT = 8;

/**
 * `previewPins`: the page was asked for the first pictures of each gallery. A server that didn't
 * honour it answers with the cover alone, and the card is not marked, so its gallery is read apart.
 */
export const blockCardFromHttp = (
  data: z.infer<typeof blockCard$>,
  previewPins = false
): ShortlistBlockCard => ({
  id: data.id,
  contactId: data.contact_id,
  order: data.order,
  note: data.data?.paragraph ?? null,
  contact: data.contact,
  listCounts: data.contact?.list_counts ?? {},
  pins: previewPins ? blockPinsFromHttp(data.pins) : data.pins,
  pinCount: data.pin_count,
  ...(previewPins &&
    data.pins.length >= Math.min(data.pin_count, PREVIEW_PINS_LIMIT) && {
      pinsArePreview: true
    }),
  upvotes: data.upvotes,
  downvotes: data.downvotes,
  comments: data.comments.map(({ author, ...comment }) => ({
    ...comment,
    author: author
      ? {
          name: author.name,
          // the account's stored image; the avatar's url is built as v1 builds it
          pictureUrl: author.picture_url
            ? getAvatarImageURL(author.picture_url, 'xs')
            : null
        }
      : null
  })),
  upvoteCount: data.upvote_count,
  downvoteCount: data.downvote_count,
  commentCount: data.comment_count,
  bookingEvents: data.booking_events ?? null
});

export const blockPage$ = z.object({
  results: z.array(blockCard$),
  statuses: z.array(withId<Status>()).optional(),
  paging: z.object({
    total: z.number(),
    page: z.number(),
    count_per_page: z.number()
  })
});

const branding$ = z.object({
  public_company_name: z.unknown().default(undefined),
  public_team_name: z.unknown().default(undefined),
  public_brand_image: z.unknown().default(undefined),
  public_theme: z.unknown().default(undefined),
  defaults_locale: z.unknown().default(undefined)
});

const fieldDefinition$ = z.object({
  id: z.number(),
  data_type: z.custom<CustomFieldType>(value => typeof value === 'string')
});

/** Every custom field the card fields name, the groups' own fields too. */
const fieldDefinitions$ = z.array(fieldDefinition$.passthrough());
const fieldGroupDefinitions$ = z.array(
  z
    .object({ custom_field_definitions: fieldDefinitions$.nullish() })
    .passthrough()
);

export const clientSummary$ = z.discriminatedUnion('restricted', [
  z.object({
    restricted: z.literal(true),
    title: z.string().nullable(),
    version: version$,
    branding: branding$
  }),
  z.object({
    restricted: z.literal(false),
    access: z.enum(['member', 'guest']),
    published: z.boolean().optional(),
    archived: z.boolean().optional(),
    version: version$,
    shortlist: clientShortlist$,
    branding: branding$,
    qr: qr$,
    // what the pronouns beside a name need: a guest has no app settings to read them from
    global_settings: z.object({
      core_fields_pronouns_options: z.array(
        z.object({
          id: z.number(),
          value: z.string(),
          order: z.number().nullish()
        })
      ),
      pronouns_enabled_resource_type_ids: z.array(z.number())
    }),
    custom_field_definitions: fieldDefinitions$,
    custom_fieldgroup_definitions: fieldGroupDefinitions$
  })
]);

export const clientSummaryFromHttp = (
  data: z.infer<typeof clientSummary$>
): ClientSummary => {
  if (data.restricted) {
    return {
      restricted: true,
      title: data.title,
      version: data.version,
      branding: data.branding
    };
  }
  const definitions = [
    ...data.custom_field_definitions,
    ...data.custom_fieldgroup_definitions.flatMap(
      group => group.custom_field_definitions ?? []
    )
  ];
  return {
    restricted: false,
    access: data.access,
    published: data.published ?? null,
    archived: data.archived ?? null,
    title: data.shortlist.public_title,
    version: data.version,
    branding: data.branding,
    shortlist: clientShortlistFromHttp(data.shortlist),
    qr: data.qr,
    customFieldDefinitions: definitions,
    fieldMappings: Object.fromEntries(
      definitions.map(definition => [
        `custom_${definition.id}`,
        definition.data_type
      ])
    ),
    pronouns: {
      options: data.global_settings.core_fields_pronouns_options.map(
        option => ({ ...option, order: option.order ?? null })
      ),
      enabledResourceTypeIds:
        data.global_settings.pronouns_enabled_resource_type_ids
    }
  };
};

export const blockPins$ = z.object({
  results: z.array(withId<Pin>())
});

/** The live pins, by `order`, then as they were added; the board's cards read them the same way. */
export const blockPinsFromHttp = (pins: ReadonlyArray<Pin>): Pin[] =>
  pins
    .filter(pin => !pin.archived)
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) -
          (b.order ?? Number.MAX_SAFE_INTEGER) || Number(a.id) - Number(b.id)
    );
